import React, { useEffect, useRef, useState } from "react";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { GOOGLE_MAPS_API_KEY } from "../../utils/constants";
import {
  parseAddressComponents,
  toGoogleLanguage,
  toGoogleRegionCode,
  type ParsedStreetAddress,
} from "./parseGoogleAddress";

export type { ParsedStreetAddress };

interface AddressSuggestionOption {
  label: string;
  placePrediction: google.maps.places.PlacePrediction;
}

export interface AddressStreetAutocompleteProps {
  language: string;
  country: string;
  value: string;
  onChange: (street: string) => void;
  onAddressSelect: (address: ParsedStreetAddress) => void;
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
}

const MIN_INPUT_LENGTH = 3;
const DEBOUNCE_MS = 300;
// German street names are usually typed as "route"; without it autocomplete returns [].
const STREET_PRIMARY_TYPES = [
  "premise",
  "subpremise",
  "street_address",
  "route",
];

const AddressStreetAutocomplete: React.FC<AddressStreetAutocompleteProps> = ({
  language,
  country,
  value,
  onChange,
  onAddressSelect,
  label,
  required = false,
  error = false,
  helperText,
  fullWidth = true,
}) => {
  const [options, setOptions] = useState<AddressSuggestionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [placesReady, setPlacesReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const placesLibraryRef = useRef<google.maps.PlacesLibrary | null>(null);
  const sessionTokenRef = useRef<
    google.maps.places.AutocompleteSessionToken | undefined
  >(undefined);
  const activeRequestRef = useRef(0);
  const googleLanguage = toGoogleLanguage(language);

  useEffect(() => {
    const apiKey = GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    void import("./googlePlacesLoader")
      .then(({ loadGooglePlacesLibrary }) => loadGooglePlacesLibrary(apiKey))
      .then((lib) => {
        placesLibraryRef.current = lib;
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
        setPlacesReady(true);
      })
      .catch(() => setLoadFailed(true));
  }, []);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || !placesReady) {
      setOptions([]);
      return;
    }

    const trimmed = value.trim();
    if (trimmed.length < MIN_INPUT_LENGTH) {
      setOptions([]);
      setLoading(false);
      return;
    }

    const requestId = ++activeRequestRef.current;
    setLoading(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const lib = placesLibraryRef.current!;
          if (requestId !== activeRequestRef.current) return;

          const request: google.maps.places.AutocompleteRequest = {
            input: trimmed,
            sessionToken: sessionTokenRef.current,
            includedPrimaryTypes: STREET_PRIMARY_TYPES,
            language: googleLanguage,
          };

          const regionCode = toGoogleRegionCode(country);
          if (regionCode) {
            request.includedRegionCodes = [regionCode];
          }

          const { suggestions } =
            await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              request,
            );

          if (requestId !== activeRequestRef.current) return;

          setOptions(
            suggestions
              .map((suggestion) => suggestion.placePrediction)
              .filter(
                (
                  prediction,
                ): prediction is google.maps.places.PlacePrediction =>
                  !!prediction,
              )
              .map((prediction) => ({
                label: prediction.text.text,
                placePrediction: prediction,
              })),
          );
        } catch {
          if (requestId === activeRequestRef.current) {
            setOptions([]);
          }
        } finally {
          if (requestId === activeRequestRef.current) {
            setLoading(false);
          }
        }
      })();
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [placesReady, value, country, googleLanguage]);

  const handleSelect = async (option: AddressSuggestionOption | null) => {
    if (!option || !placesLibraryRef.current) return;

    try {
      const lib = placesLibraryRef.current;
      const place = option.placePrediction.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });

      const parsed = parseAddressComponents(place.addressComponents ?? []);
      onChange(parsed.street || option.label);
      onAddressSelect(parsed);

      sessionTokenRef.current = new lib.AutocompleteSessionToken();
      setOptions([]);
    } catch {
      onChange(option.label);
    }
  };

  if (!GOOGLE_MAPS_API_KEY || loadFailed) {
    return (
      <TextField
        fullWidth={fullWidth}
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        error={error}
        helperText={helperText}
        slotProps={{
          htmlInput: { autoComplete: "new-password" },
        }}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      filterOptions={(items) => items}
      options={options}
      loading={loading || !placesReady}
      inputValue={value}
      onInputChange={(_event, newValue, reason) => {
        if (reason === "input" || reason === "clear") {
          onChange(newValue);
        }
      }}
      onChange={(_event, newValue) => {
        if (!newValue || typeof newValue === "string") return;
        void handleSelect(newValue);
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      isOptionEqualToValue={(option, selected) =>
        typeof option === "string" || typeof selected === "string"
          ? option === selected
          : option.label === selected.label
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps} sx={{ py: 1 }}>
            <LocationOnOutlinedIcon
              fontSize="small"
              sx={{ mr: 1.5, color: "text.secondary", flexShrink: 0 }}
            />
            <Typography variant="body2" sx={{ minWidth: 0 }}>
              {option.label}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <LocationOnOutlinedIcon
                    fontSize="small"
                    sx={{ ml: 0.5, mr: 0.5, color: "action.active" }}
                  />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading || !placesReady ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              autoComplete: "new-password",
            },
          }}
        />
      )}
    />
  );
};

export default AddressStreetAutocomplete;
